// src/pages/help/SupportTicketsTab.tsx

import React, { useState, useEffect } from "react";
import { Typography } from "antd";
import { PlusOutlined, ReloadOutlined, CustomerServiceOutlined } from "@ant-design/icons";
import { getSessionUser } from "../../utils/auth/sessionUser";
import dayjs from "dayjs";
import { getSupportTickets } from "../../api/supportTickets/supportTicketsApi";
import { SupportTicket } from "../../types/communications/supportTickets";
import CreateSupportTicketModal from "./CreateSupportTicketModal";
import SupportTicketDetailModal from "./SupportTicketDetailModal";
import { SectionCard, InlineLoading, EmptyState, DataTable, PageToolbar, StatusTag } from "../../components/shared";
import { useNotifications } from "../../context/NotificationContext";

const { Text } = Typography;

const SupportTicketsTab: React.FC = () => {
	const { showError } = useNotifications();
	const [tickets, setTickets] = useState<SupportTicket[]>([]);
	const [loading, setLoading] = useState(false);
	const [createModalOpen, setCreateModalOpen] = useState(false);
	const [detailModalOpen, setDetailModalOpen] = useState(false);
	const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);

	const user = getSessionUser();
	const userId = user.id;

	useEffect(() => {
		loadTickets();
	}, [userId]);

	const loadTickets = async () => {
		setLoading(true);
		try {
			const data = await getSupportTickets({ user_id: userId });
			setTickets(data);
		} catch (error) {
			showError("Failed to load support tickets");
			console.error("Error loading tickets:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleCreateSuccess = () => {
		setCreateModalOpen(false);
		loadTickets();
	};

	const handleViewTicket = (ticket: SupportTicket) => {
		setSelectedTicket(ticket);
		setDetailModalOpen(true);
	};

	const handleDetailClose = () => {
		setDetailModalOpen(false);
		setSelectedTicket(null);
		loadTickets();
	};

	const columns = [
		{
			title: "Subject",
			dataIndex: "subject",
			key: "subject",
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 150,
			render: (status: string) => <StatusTag status={status} />,
		},
		{
			title: "Priority",
			dataIndex: "priority",
			key: "priority",
			width: 100,
			render: (priority: string) => <StatusTag status={priority} label={priority.charAt(0).toUpperCase() + priority.slice(1)} />,
		},
		{
			title: "Created",
			dataIndex: "created_at",
			key: "created_at",
			width: 180,
			render: (date: string) => dayjs(date).format("DD/MM/YYYY HH:mm"),
		},
	];

	return (
		<>
			<PageToolbar
				primaryAction={{
					label: "New Ticket",
					icon: <PlusOutlined />,
					onClick: () => setCreateModalOpen(true),
				}}
				extraActions={[
					{
						label: "Refresh",
						icon: <ReloadOutlined />,
						onClick: loadTickets,
					},
				]}
			/>

			<SectionCard>
				{loading ? (
					<InlineLoading minHeight={300} message="Loading support tickets…" />
				) : tickets.length === 0 ? (
					<EmptyState
						icon={<CustomerServiceOutlined />}
						title="No support tickets yet"
						description="Create a new ticket to get help from our support team"
					/>
				) : (
					<DataTable columns={columns} dataSource={tickets} rowKey="id" onRowClick={(row) => handleViewTicket(row)} />
				)}
			</SectionCard>

			<CreateSupportTicketModal open={createModalOpen} onClose={() => setCreateModalOpen(false)} onSuccess={handleCreateSuccess} />

			{selectedTicket && <SupportTicketDetailModal open={detailModalOpen} ticket={selectedTicket} onClose={handleDetailClose} isAdmin={false} />}
		</>
	);
};

export default SupportTicketsTab;
